import * as React from 'react';

import { connect } from 'react-redux';

import styled from 'styled-components';

import AppState from '@webapp/store/state';

import { Translate } from 'react-localize-redux';

import { normalizeTrackName } from '@webapp/utils/normalizers';

import SimpleTrack from '@common/types/release/simple-track';

import { Ownership } from '@services/ownership/types/ownership';
import { Collaborator } from '@services/collaborator/types/collaborator';

import Chip from '@components/basics/chip';
import Flex from '@components/layout/flex';
import Typography from '@components/basics/typography';
import CleanIcon from '@components/basics/icons/track/clean-icon';
import CircleArrowIcon from '@components/basics/icons/circle-arrow';
import ExplicitIcon from '@components/basics/icons/track/explicit-icon';
import AvatarPreview from '@components/layout/preview-box/avatar-preview';
import InstrumentalIcon from '@components/basics/icons/track/instrumental-icon';
import InstantGratificationIcon from '@components/basics/icons/track/instant-gratification-icon';

interface SongInfoRowProps {
  className?: string;
  track: Partial<SimpleTrack>;
  number: number;
  songName: string;
  featuring?: string;
  artist: Collaborator[];
  artistArray: { name: string; avatar: string }[];
  featuringArray: { name: string; avatar: string }[];
  composerArray: Ownership[];
  owners: Ownership[];
  hasCredits: boolean;
  credits?: { name: string; avatar: string }[];
  isClean?: boolean;
  isExplicit?: boolean;
  isInstrumental?: boolean;
  isInstantGratification?: boolean;
  isNFTExlusive?: boolean;
  hasLyrics: boolean;
  isActive?: boolean;
  language: string;
  lyrics?: boolean;
}

interface StateProps {
  trackTitle: string;
  isMobile: boolean;
}

const mapStateToProps = (state: AppState, props: SongInfoRowProps) => ({
  trackTitle: normalizeTrackName(props.track, state),
  isMobile: state.ui.mobile,
});
type activeType = 'co' | 'so' | 'mo' | 'cr';
export const SongInfoRow: React.FC<SongInfoRowProps & StateProps> = (props) => {
  const [active, setActive] = React.useState<activeType[]>([]);

  const dropdownItem = (type: activeType) => {
    return (
      <CircleArrowIcon
        darkCustomBgColor
        size="24px"
        arrowUp={active.includes(type)}
        onClick={() => {
          if (active.includes(type))
            setActive(active.filter((a) => a !== type));
          if (!active.includes(type)) setActive([...active, type]);
        }}
      />
    );
  };

  return (
    <div className={props.className}>
      {!props.isMobile ? (
        <TableWrapper>
          <div className="track-number">
            <CircleWrapper>
              <Typography weight="700" className="circle-text">
                {props.number < 9 ? `0${props.number}` : props.number}
              </Typography>
            </CircleWrapper>
          </div>
          <div className="song-name">
            <div>
              <Typography className="track-name" weight="700">
                {props.trackTitle}
              </Typography>
              <Flex alignItems="center" gap={3}>
                {props.isClean && <CleanIcon isInvertedTheme />}
                {props.isExplicit && <ExplicitIcon isInvertedTheme />}
                {props.isInstrumental && <InstrumentalIcon isInvertedTheme />}
                {props.isInstantGratification && (
                  <InstantGratificationIcon isInvertedTheme />
                )}
                {props.isNFTExlusive && (
                  <Chip smaller smallFont chipType="blue">
                    <Translate id="RELEASE.SIDEBAR.LIST.NFT_EXCLUSIVE" />
                  </Chip>
                )}
                {(props.isClean ||
                  props.isExplicit ||
                  props.isInstrumental ||
                  props.isInstantGratification ||
                  props.isNFTExlusive) &&
                  props.language && <Typography type="small"> • </Typography>}
                {props.language && (
                  <Typography type="small" uppercase>
                    {props.language}
                  </Typography>
                )}
                {props.lyrics && props.language && (
                  <Typography type="small"> • </Typography>
                )}
                {props.lyrics && <Typography type="small">Lyrics</Typography>}
              </Flex>
            </div>
          </div>

          <div className="feat">
            <AvatarPreview
              avatarSize="24"
              items={props.artistArray.concat(props.featuringArray)}
              breakingIndex={2}
            />
          </div>

          <div className="composer">
            <AvatarPreview
              avatarSize="24"
              items={props.composerArray.map((a) => {
                return { name: a.name || a.contact, avatar: a?.avatar ?? '' };
              })}
              breakingIndex={2}
            />
          </div>

          <div className="owners">
            <AvatarPreview
              avatarSize="24"
              items={props.owners.map((a) => {
                return { name: a.name || a.contact, avatar: a?.avatar ?? '' };
              })}
              breakingIndex={2}
            />
          </div>

          <div className="credits">
            <AvatarPreview
              avatarSize="24"
              noAvatar
              items={props.credits ?? []}
              breakingIndex={2}
            />
          </div>
        </TableWrapper>
      ) : (
        <TableWrapper>
          <div className="row first">
            <div className="track-number">
              <CircleWrapper>
                <Typography weight="700" type="smaller">
                  {props.number < 9 ? `0${props.number}` : props.number}
                </Typography>
              </CircleWrapper>
            </div>
            <div className="song-name">
              <div>
                <Typography className="track-name" weight="700">
                  {props.trackTitle}
                </Typography>
                <Flex alignItems="center" gap={3}>
                  {props.isClean && <CleanIcon isInvertedTheme />}
                  {props.isExplicit && <ExplicitIcon isInvertedTheme />}
                  {props.isInstrumental && <InstrumentalIcon isInvertedTheme />}
                  {props.isInstantGratification && (
                    <InstantGratificationIcon isInvertedTheme />
                  )}
                  {props.isNFTExlusive && (
                    <Chip smaller smallFont chipType="blue">
                      <Translate id="RELEASE.SIDEBAR.LIST.NFT_EXCLUSIVE" />
                    </Chip>
                  )}
                  {(props.isClean ||
                    props.isExplicit ||
                    props.isInstrumental ||
                    props.isInstantGratification ||
                    props.isNFTExlusive) &&
                    props.language && <Typography type="small"> • </Typography>}
                  {props.language && (
                    <Typography type="small" uppercase>
                      {props.language}
                    </Typography>
                  )}
                  {props.lyrics && props.language && (
                    <Typography type="small"> • </Typography>
                  )}
                  {props.lyrics && <Typography type="small">Lyrics</Typography>}
                </Flex>
              </div>
            </div>
          </div>

          <div className="row">
            <Typography weight="700" type="small">
              <Translate id="RELEASE.PREVIEW.SONG_LIST.COLLABORATORS" />
            </Typography>
            <div className="dropdown">
              <AvatarPreview
                avatarSize="24"
                items={props.artistArray.concat(props.featuringArray)}
                breakingIndex={
                  props?.artistArray?.concat(props.featuringArray)?.length
                }
              />
              {dropdownItem('co')}
            </div>
          </div>

          <div className="row">
            <Typography weight="700" type="small">
              <Translate id="RELEASE.PREVIEW.SONG_LIST.SONGWRITERS" />
            </Typography>
            <div>
              <div className="dropdown">
                <AvatarPreview
                  avatarSize="24"
                  items={props.composerArray.map((a) => {
                    return {
                      name: a.name || a.contact,
                      avatar: a?.avatar ?? '',
                    };
                  })}
                  breakingIndex={props?.composerArray?.length}
                />
                {dropdownItem('so')}
              </div>
            </div>
          </div>
          {active.includes('so') && (
            <DropdownWrapper>
              {props.composerArray?.map((composer, i) => (
                <Flex
                  justifyContent="space-between"
                  alignItems="center"
                  className="dropdown-item"
                  key={`tooltip-${i}-${composer.name}`}
                >
                  <Typography className="dropdown-text">
                    {composer.name || composer.contact || ''}
                  </Typography>
                  <Typography
                    className="dropdown-text"
                    weight="700"
                    align="right"
                  >
                    {composer.split}%
                  </Typography>
                </Flex>
              ))}
            </DropdownWrapper>
          )}
          <div className="row">
            <Typography weight="700" type="small">
              <Translate id="RELEASE.PREVIEW.SONG_LIST.OWNERS" />
            </Typography>
            <div className="dropdown">
              <AvatarPreview
                avatarSize="24"
                items={props.owners.map((a) => {
                  return { name: a.name || a.contact, avatar: a?.avatar ?? '' };
                })}
                breakingIndex={
                  props.owners.map((a) => {
                    return {
                      name: a.name || a.contact,
                      avatar: a?.avatar ?? '',
                    };
                  })?.length
                }
              />
              {dropdownItem('mo')}
            </div>
          </div>
          {active.includes('mo') && (
            <DropdownWrapper>
              {props.owners?.map((owner, i) => (
                <Flex
                  justifyContent="space-between"
                  alignItems="center"
                  className="dropdown-item"
                  key={`tooltip-${i}-${owner.name}`}
                >
                  <Typography className="dropdown-text">
                    {owner.name || owner.contact || ''}
                  </Typography>
                  <Typography
                    className="dropdown-text"
                    weight="700"
                    align="right"
                  >
                    {owner.split}%
                  </Typography>
                </Flex>
              ))}
            </DropdownWrapper>
          )}
          <div className="row">
            <Typography weight="700" type="small">
              <Translate id="RELEASE.PREVIEW.SONG_LIST.CREDITS" />
            </Typography>
            <div className="dropdown">
              <AvatarPreview
                avatarSize="24"
                noAvatar
                items={props.credits ?? []}
                breakingIndex={props?.credits?.length ?? 0}
              />
              {dropdownItem('cr')}
            </div>
          </div>
        </TableWrapper>
      )}
    </div>
  );
};

export default connect(mapStateToProps)(SongInfoRow);

const TableWrapper = styled.div`
  padding: 20px;
  background-color: ${({ theme }) => theme.colors.box};
  border-radius: 10px;
  display: grid;
  grid-gap: 15px;
  align-items: center;
  grid-template-columns: repeat(9, minmax(0, 1fr));

  @media ${({ theme }) => theme.device.tablet} {
    padding: 30px;
  }

  .row {
    grid-column: span 9;
    display: flex;
    align-items: center;
    justify-content: space-between;

    &.first {
      gap: 15px;
      justify-content: flex-start;
    }

    .dropdown {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 10px;
    }
  }

  .track-number {
    @media ${({ theme }) => theme.device.tablet} {
      margin: 0 auto;
    }
  }

  .song-name {
    display: flex;
    align-items: center;
    gap: 10px;

    @media ${({ theme }) => theme.device.tablet} {
      grid-column: 2 / 6;
    }

    .track-name {
      margin-bottom: 5px;
      overflow: hidden;
      text-overflow: ellipsis;
      width: 250px;
      @media ${({ theme }) => theme.device.tablet} {
        width: 200px;
      }
    }
  }
`;

const CircleWrapper = styled.div`
  display: flex;
  text-align: center;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 50%;
  border: 2px solid ${({ theme }) => theme.colors.primary};
  width: 24px;
  height: 24px;

  .circle-text {
    font-size: ${({ theme }) => theme.typography.small};
  }
`;

const DropdownWrapper = styled.div`
  grid-column: span 9;
  display: grid;
  grid-gap: 5px;

  .dropdown-item {
    .dropdown-text {
      font-size: ${({ theme }) => theme.typography.span};
    }
  }
`;
